import type Phaser from "phaser";

import type { ArenaConfig } from "../config/gameplay-config";
import { gameplayConfig } from "../config/gameplay-config";
import { motionConfig, type MotionProfile } from "../config/motion-config";
import {
  formatOpponentLevelCue,
  isOpponentCueVisible,
} from "../application/strength-readability";
import type { ActiveBotEntity } from "../application/bot-runtime";
import type { PlayerMovementState } from "../simulation/player-movement";
import type { TailClipRecord } from "../application/tail-clip-runtime";
import { getPlayerBodyRippleScale } from "../simulation/player-body-trail";
import {
  drawBotBand,
  drawSnakeBody,
  drawSnakeHead,
  drawSnakeTailPoint,
  getBotBandPattern,
  getSnakeVisualDirection,
  type SnakeBodyVisualSegment,
} from "./snake-visual-renderer";
import {
  getSnakeChompStartedAtMs,
  getSnakeFacePose,
  getSnakeMotionSegments,
} from "./snake-motion";

type BotLabel = Pick<
  Phaser.GameObjects.Text,
  "destroy" | "setDepth" | "setOrigin" | "setPosition" | "setText"
>;

type BotLabelFactory = (
  x: number,
  y: number,
  text: string,
  style: Phaser.Types.GameObjects.Text.TextStyle,
) => BotLabel;

export interface BotTailSegment {
  readonly position: {
    readonly x: number;
    readonly y: number;
  };
  readonly radius: number;
}

export const calculateBotTailSegments = (
  bot: ActiveBotEntity,
  arena: ArenaConfig,
): readonly BotTailSegment[] => {
  void arena;
  return bot.body.segments;
};

export class BotRenderer {
  private readonly labels = new Map<string, BotLabel>();

  public constructor(
    private readonly graphics: Phaser.GameObjects.Graphics,
    private readonly createLabel: BotLabelFactory,
    private readonly arena: ArenaConfig,
  ) {}

  public destroy(): void {
    for (const label of this.labels.values()) {
      label.destroy();
    }
    this.labels.clear();
    this.graphics.destroy();
  }

  public getActiveLabelCount(): number {
    return this.labels.size;
  }

  public render(
    bots: readonly ActiveBotEntity[],
    player: PlayerMovementState | null,
    simulationTimeMs = 0,
    motionProfile: MotionProfile = motionConfig.standard,
    lastClip: TailClipRecord | null = null,
  ): void {
    this.graphics.clear();
    const visibleBots =
      player === null
        ? []
        : bots.filter((bot) =>
            isOpponentCueVisible(
              player.position,
              bot.position,
              gameplayConfig.strengthReadability.opponentCueDistance,
            ),
          );
    const activeIds = new Set(visibleBots.map((bot) => bot.id));
    for (const [id, label] of this.labels) {
      if (!activeIds.has(id)) {
        label.destroy();
        this.labels.delete(id);
      }
    }
    for (const bot of bots) {
      this.drawBot(bot, simulationTimeMs, motionProfile, lastClip);
    }
    if (player !== null) {
      for (const bot of visibleBots) {
        const levelCue = bot.traits.challenger
          ? bot.cosmetic.strengthCue
          : formatOpponentLevelCue(bot.traits.level);
        const label = this.labels.get(bot.id) ?? this.addLabel(bot, levelCue);
        label
          .setPosition(
            bot.position.x,
            bot.position.y - bot.collision.radius - 10,
          )
          .setText(levelCue);
      }
    }
  }

  private addLabel(bot: ActiveBotEntity, levelCue: string): BotLabel {
    const label = this.createLabel(
      bot.position.x,
      bot.position.y - bot.collision.radius - 10,
      levelCue,
      {
        backgroundColor: bot.traits.challenger ? "#ffd166" : "#102a43",
        color: bot.traits.challenger ? "#102a43" : "#f7fff7",
        fontFamily: "Arial, sans-serif",
        fontSize: "20px",
        fontStyle: "bold",
        padding: { x: 7, y: 3 },
      },
    );
    label.setOrigin(0.5, 1).setDepth(40);
    this.labels.set(bot.id, label);
    return label;
  }

  private drawBot(
    bot: ActiveBotEntity,
    simulationTimeMs: number,
    motionProfile: MotionProfile,
    lastClip: TailClipRecord | null,
  ): void {
    const radius = bot.collision.radius;
    const segments = calculateBotTailSegments(bot, this.arena);
    const drawableSegments = segments.map((segment, index) => ({
      index,
      segment: {
        position: segment.position,
        radius:
          segment.radius *
          getPlayerBodyRippleScale(
            bot.body,
            index,
            simulationTimeMs,
            motionProfile.bodyRippleScale,
            gameplayConfig.playerBody,
          ) *
          gameplayConfig.snakeVisual.bodyOverlapScale *
          gameplayConfig.snakeVisual.bodyWidthScale,
      } satisfies SnakeBodyVisualSegment,
    }));
    const visualSegments = getSnakeMotionSegments(
      bot.id,
      bot.position,
      drawableSegments.map(({ segment }) => segment),
      bot.directionRadians,
      simulationTimeMs,
      motionProfile.snakeMotionScale,
      gameplayConfig.snakeMotion,
    );
    drawSnakeBody(
      this.graphics,
      { position: bot.position, radius },
      visualSegments,
      bot.cosmetic.bodyColor,
      bot.directionRadians,
      gameplayConfig.snakeVisual,
    );
    const bandPattern = getBotBandPattern(bot.id);
    visualSegments.forEach((segment, index) => {
      const leader = visualSegments[index - 1]?.position ?? bot.position;
      drawBotBand(
        this.graphics,
        segment.position,
        segment.radius,
        getSnakeVisualDirection(leader, segment.position, bot.directionRadians),
        bot.cosmetic.accentColor,
        index + 1,
        bandPattern,
      );
    });
    const tail = visualSegments[visualSegments.length - 1];
    const tailLeader = visualSegments[visualSegments.length - 2];
    if (tail !== undefined && tailLeader !== undefined) {
      drawSnakeTailPoint(
        this.graphics,
        tailLeader.position,
        tail.position,
        tail.radius,
        bot.cosmetic.bodyColor,
        bot.directionRadians,
        gameplayConfig.snakeVisual,
      );
    }
    const face = getSnakeFacePose(
      bot.id,
      simulationTimeMs,
      getSnakeChompStartedAtMs(
        bot.id,
        bot.lastPickup?.occurredAtMs ?? null,
        lastClip,
      ),
      motionProfile.snakeMotionScale,
      gameplayConfig.snakeMotion,
    );
    drawSnakeHead(
      this.graphics,
      {
        accentColor: bot.cosmetic.accentColor,
        bodyColor: bot.cosmetic.bodyColor,
        directionRadians: bot.directionRadians,
        face,
        pattern: null,
        position: bot.position,
        radius,
      },
      gameplayConfig.snakeVisual,
    );
  }
}
